// src/pages/vendor/Payouts.jsx - COMPLETE UPDATED
import React, { useState, useEffect } from 'react';
import { vendorApi } from '../../api/vendorApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Cancel,
  Pending,
  Edit,
  DeleteOutline,
  AccountBalanceWallet,
  TrendingUp,
  AttachMoney,
  Info,
  Refresh,
  Receipt,
  Warning,
  Percent,
} from '@mui/icons-material';

import {
  formatCurrency,
  formatDate,
  getStatusColor,
  getStatusIcon,
  getPayoutStatusInfo,
  getPayoutMethod,
  getPayoutMethodName,
  PAYOUT_METHODS,
  calculateFinancialSummary,
} from '../../services/financeService';

const Payouts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [availableBalance, setAvailableBalance] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [pendingPayouts, setPendingPayouts] = useState(0);
  const [processingPayouts, setProcessingPayouts] = useState(0);
  const [totalCommission, setTotalCommission] = useState(0);
  const [commissionRate, setCommissionRate] = useState(0);
  const [minimumPayout, setMinimumPayout] = useState(500);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [amount, setAmount] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingPayout, setEditingPayout] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Payout Method State
  const [payoutMethod, setPayoutMethod] = useState('bank_transfer');
  const [payoutPhoneNumber, setPayoutPhoneNumber] = useState('');
  const [hasBankDetails, setHasBankDetails] = useState(true);
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    bankAccountName: '',
    bankAccountNumber: '',
  });

  const stats = {
    availableBalance,
    totalRevenue,
    totalCommission,
    totalPayouts: totalWithdrawn,
    pendingPayouts,
    processingPayouts,
    totalWithdrawn,
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApi.getPayouts();
      const data = response || {};
      
      // Use the shared calculation
      const payoutsList = data.history || data.payouts || [];
      const calculatedStats = calculateFinancialSummary(payoutsList);
      
      setAvailableBalance(data.availableBalance || calculatedStats.availableBalance || 0);
      setTotalRevenue(data.totalRevenue || calculatedStats.totalRevenue || 0);
      setTotalWithdrawn(data.totalWithdrawn || calculatedStats.totalWithdrawn || 0);
      setPendingPayouts(data.totalPending || calculatedStats.pendingPayouts || 0);
      setProcessingPayouts(data.totalProcessing || calculatedStats.processingPayouts || 0);
      setTotalCommission(data.totalCommission || calculatedStats.totalCommission || 0);
      setCommissionRate(data.commissionRate || 0);
      setMinimumPayout(data.minimumPayout || 500);
      setPayouts(payoutsList);
      setPayoutMethod(data.payoutMethod || 'bank_transfer');
      setPayoutPhoneNumber(data.payoutPhoneNumber || '');
      
      // Check bank details
      if (data.bankName && data.bankAccountNumber) {
        setHasBankDetails(true);
        setBankDetails({
          bankName: data.bankName || '',
          bankAccountName: data.bankAccountName || '',
          bankAccountNumber: data.bankAccountNumber || '',
        });
      } else {
        setHasBankDetails(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to load payout data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
    showSnackbar('Payout data refreshed', 'info');
  };

  const getSelectedMethod = () => {
    return getPayoutMethod(payoutMethod);
  };

  const getSelectedMethodName = () => {
    return getPayoutMethodName(payoutMethod);
  };

  const handleRequestPayout = async () => {
    const amountNum = parseFloat(amount);
    if (!amount || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (amountNum < minimumPayout) {
      setError(`Minimum withdrawal amount is ${formatCurrency(minimumPayout)}`);
      return;
    }
    if (amountNum > availableBalance) {
      setError(`Insufficient balance. Available: ${formatCurrency(availableBalance)}`);
      return;
    }

    const selectedMethod = getSelectedMethod();
    if (selectedMethod?.requiresBank && !hasBankDetails) {
      setError('Please set up your bank details in your vendor profile first');
      return;
    }
    if (selectedMethod?.requiresPhone && !payoutPhoneNumber) {
      setError('Please set up your phone number for mobile money payouts in your vendor profile');
      return;
    }

    try {
      await vendorApi.requestPayout({ 
        amount: amountNum,
        payoutMethod: payoutMethod,
        payoutPhoneNumber: payoutPhoneNumber,
      });
      showSnackbar('Withdrawal request submitted successfully!', 'success');
      setOpenDialog(false);
      setAmount('');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to request payout');
    }
  };

  const handleSaveEdit = async () => {
    const newAmount = parseFloat(editAmount);
    if (!newAmount || newAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (newAmount < minimumPayout) {
      setError(`Minimum withdrawal amount is ${formatCurrency(minimumPayout)}`);
      return;
    }
    if (newAmount > availableBalance + parseFloat(editingPayout?.amount || 0)) {
      setError('Insufficient available balance');
      return;
    }

    try {
      await vendorApi.updatePayoutAmount(editingPayout.id, newAmount);
      showSnackbar('Payout amount updated successfully!', 'success');
      setOpenEditDialog(false);
      setEditingPayout(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update payout amount');
    }
  };

  const handleCancelPayout = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this payout request?')) return;

    try {
      await vendorApi.cancelPayout(id);
      showSnackbar('Payout request cancelled successfully.', 'success');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel payout request');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading payout information...
        </Typography>
      </Box>
    );
  }

  const selectedMethod = getSelectedMethod();

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Payouts & Withdrawals
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track your earnings and request payouts via your preferred payment method
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={isRefreshing}
              size={isMobile ? 'small' : 'medium'}
            >
              {isRefreshing ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Bank Details Warning */}
      {selectedMethod?.requiresBank && !hasBankDetails && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning />
            <Typography variant="body2">
              Please set up your bank details in your vendor profile before requesting a bank transfer payout.
            </Typography>
          </Stack>
        </Alert>
      )}

      {selectedMethod?.requiresPhone && !payoutPhoneNumber && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning />
            <Typography variant="body2">
              Please set up your phone number in your vendor profile for {selectedMethod.name} payouts.
            </Typography>
          </Stack>
        </Alert>
      )}

      {/* Main Balance Card */}
      <Card sx={{ 
        mb: 3, 
        bgcolor: 'primary.main', 
        color: 'white', 
        p: { xs: 2, sm: 3 }, 
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box
          sx={{
            position: 'absolute',
            right: -50,
            top: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.05)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 20,
            bottom: -80,
            width: 150,
            height: 150,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        />
        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={1}>
                <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1 }}>
                  AVAILABLE BALANCE
                </Typography>
                <Typography variant="h2" fontWeight="bold" sx={{ fontSize: { xs: '2.5rem', sm: '3.5rem' } }}>
                  {formatCurrency(availableBalance)}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Minimum withdrawal: {formatCurrency(minimumPayout)}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ 
                      bgcolor: 'white', 
                      color: 'primary.main', 
                      '&:hover': { bgcolor: 'grey.100' },
                      borderRadius: 2,
                      px: 3,
                    }}
                    disabled={availableBalance < minimumPayout || (!hasBankDetails && selectedMethod?.requiresBank)}
                  >
                    Request Withdrawal
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={2} justifyContent={isMobile ? 'flex-start' : 'flex-end'} flexWrap="wrap">
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Revenue</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(totalRevenue)}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Withdrawn</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(totalWithdrawn)}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Pending</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(pendingPayouts + processingPayouts)}</Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payment Method Info */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            <strong>Payout Method:</strong>
          </Typography>
          <Chip
            label={selectedMethod?.name || 'Bank Transfer'}
            color="primary"
            variant="outlined"
          />
          {selectedMethod?.requiresPhone && payoutPhoneNumber && (
            <Typography variant="caption" color="text.secondary">
              Phone: {payoutPhoneNumber}
            </Typography>
          )}
          {selectedMethod?.requiresBank && hasBankDetails && (
            <Typography variant="caption" color="text.secondary">
              Bank: {bankDetails.bankName}
            </Typography>
          )}
          <Button
            size="small"
            variant="text"
            onClick={() => window.location.href = '/vendor/profile'}
            sx={{ ml: 'auto' }}
          >
            Update
          </Button>
        </Stack>
      </Paper>

      {/* Quick Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <AccountBalanceWallet color="primary" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {formatCurrency(availableBalance)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Available</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <TrendingUp color="success" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {formatCurrency(totalRevenue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Pending color="warning" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {formatCurrency(pendingPayouts + processingPayouts)}
              </Typography>
              <Typography variant="caption" color="text.secondary">In Progress</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Percent color="secondary" sx={{ fontSize: { xs: 28, sm: 36 }, mb: 0.5 }} />
              <Typography variant="h6" fontWeight="bold" color="secondary.main">
                {formatCurrency(totalCommission)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Commission</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Withdrawal History */}
      <Paper sx={{ borderRadius: 3, p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">Withdrawal History</Typography>
          <Chip label={`${payouts.length} requests`} size="small" variant="outlined" />
        </Stack>
        <TableContainer>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!payouts || payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                    <Stack alignItems="center" spacing={1}>
                      <Receipt sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                      <Typography color="text.secondary">No payout history yet</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Your withdrawals will appear here once you request a payout
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                payouts.map((p, index) => {
                  const isPending = p.status === 'pending';
                  
                  return (
                    <TableRow key={p.id || index} hover>
                      <TableCell>{formatDate(p.requestedAt)}</TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" color={p.status === 'completed' ? 'success.main' : 'text.primary'}>
                          {formatCurrency(p.amount)}
                        </Typography>
                        {p.transactionRef && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Ref: {p.transactionRef}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <span>{getPayoutMethod(p.payoutMethod)?.icon || '🏦'}</span>
                          <Typography variant="body2">{getPayoutMethodName(p.payoutMethod)}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={getStatusIcon(p.status)}
                          label={p.status || 'Pending'} 
                          size="small" 
                          color={getStatusColor(p.status)}
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {isPending && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Edit Amount">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => {
                                  setEditingPayout(p);
                                  setEditAmount(p.amount.toString());
                                  setOpenEditDialog(true);
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel Request">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleCancelPayout(p.id)}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                        {p.status === 'processing' && (
                          <Chip 
                            label="Processing"
                            size="small"
                            color="info"
                            variant="outlined"
                            icon={<Pending fontSize="small" />}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Request Withdrawal Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoney color="primary" />
            <Typography variant="h6">Request Withdrawal</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info" icon={<Info />}>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  Available balance: <strong>{formatCurrency(availableBalance)}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Minimum withdrawal: {formatCurrency(minimumPayout)}
                </Typography>
              </Stack>
            </Alert>

            <Alert severity="info" icon={<Info />}>
              <Typography variant="body2">
                Payout Method: <strong>{getSelectedMethodName()}</strong>
              </Typography>
              {selectedMethod?.requiresPhone && payoutPhoneNumber && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Phone: {payoutPhoneNumber}
                </Typography>
              )}
              {selectedMethod?.requiresBank && hasBankDetails && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Bank: {bankDetails.bankName}
                </Typography>
              )}
            </Alert>
            
            {selectedMethod?.requiresBank && !hasBankDetails && (
              <Alert severity="warning" icon={<Warning />}>
                Please set up your bank details in your vendor profile before requesting a payout.
              </Alert>
            )}

            {selectedMethod?.requiresPhone && !payoutPhoneNumber && (
              <Alert severity="warning" icon={<Warning />}>
                Please set up your phone number in your vendor profile for {selectedMethod.name} payouts.
              </Alert>
            )}

            {availableBalance < minimumPayout && (
              <Alert severity="warning" icon={<Warning />}>
                Your balance is below the minimum withdrawal amount.
              </Alert>
            )}

            <TextField 
              fullWidth 
              type="number" 
              label="Amount (ETB)" 
              value={amount} 
              onChange={(e) => setAmount(e.target.value)} 
              disabled={availableBalance < minimumPayout || (!hasBankDetails && selectedMethod?.requiresBank)}
              helperText={
                availableBalance < minimumPayout 
                  ? `Minimum withdrawal is ${formatCurrency(minimumPayout)}` 
                  : `Enter amount between ${formatCurrency(minimumPayout)} and ${formatCurrency(availableBalance)}`
              }
              inputProps={{
                min: minimumPayout,
                max: availableBalance,
                step: 0.01,
              }}
              error={amount && (parseFloat(amount) < minimumPayout || parseFloat(amount) > availableBalance)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setOpenDialog(false)} fullWidth={isMobile}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleRequestPayout} 
            disabled={
              availableBalance < minimumPayout || 
              !amount || 
              parseFloat(amount) < minimumPayout ||
              parseFloat(amount) > availableBalance ||
              (selectedMethod?.requiresBank && !hasBankDetails) ||
              (selectedMethod?.requiresPhone && !payoutPhoneNumber)
            }
            fullWidth={isMobile}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Amount Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Edit color="primary" />
            <Typography variant="h6">Edit Payout Amount</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info">
              <Typography variant="body2">
                Current request: <strong>{formatCurrency(editingPayout?.amount)}</strong>
              </Typography>
            </Alert>
            <Typography variant="body2">
              Available balance: <strong>{formatCurrency(availableBalance + parseFloat(editingPayout?.amount || 0))}</strong>
            </Typography>
            <TextField 
              fullWidth 
              type="number" 
              label="New Amount (ETB)" 
              value={editAmount} 
              onChange={(e) => setEditAmount(e.target.value)} 
              helperText={`Minimum: ${formatCurrency(minimumPayout)}`}
              inputProps={{
                min: minimumPayout,
                max: availableBalance + parseFloat(editingPayout?.amount || 0),
                step: 0.01,
              }}
              error={editAmount && (parseFloat(editAmount) < minimumPayout || parseFloat(editAmount) > availableBalance + parseFloat(editingPayout?.amount || 0))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setOpenEditDialog(false)} fullWidth={isMobile}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveEdit} 
            disabled={
              availableBalance < minimumPayout || 
              !editAmount || 
              parseFloat(editAmount) < minimumPayout ||
              parseFloat(editAmount) > availableBalance + parseFloat(editingPayout?.amount || 0)
            }
            fullWidth={isMobile}
          >
            Update Amount
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payouts;